import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

export default function Comments({ focusItemId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/comments?focus_item_id=${focusItemId}`);
      setComments(r.data.comments || []);
    } catch (e) { console.error("comments load failed", e); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [focusItemId]);

  const post = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      const r = await api.post("/comments", { focus_item_id: focusItemId, body: body.trim() });
      setComments((c) => [...c, r.data.comment]);
      setBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };
  const remove = async (id) => {
    await api.delete(`/comments/${id}`);
    setComments((c) => c.filter((x) => x.id !== id));
  };

  if (!loaded) return null;

  return (
    <div data-testid={`comments-${focusItemId}`} className="mt-4 border-t border-neutral-800 pt-4">
      <div className="uppercase text-xs text-neutral-400 font-display tracking-widest mb-3">
        Conversation {comments.length > 0 && <span className="text-red-500">· {comments.length}</span>}
      </div>
      {comments.length === 0 && <div className="text-sm text-neutral-500 mb-3">No notes yet. Drop the first one.</div>}
      <div className="space-y-3">
        {comments.map((c) => (
          <div key={c.id} className={`rounded-md p-3 ${c.author_role === "coach" ? "bg-red-950/30 border border-red-900/50" : "bg-neutral-950 border border-neutral-800"}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-semibold text-white">{c.author_name}</span>
                <span className={`ml-2 text-[10px] uppercase font-display font-bold tracking-widest px-1.5 py-0.5 rounded ${c.author_role === "coach" ? "bg-red-600 text-white" : "bg-neutral-800 text-neutral-300"}`}>{c.author_role}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-neutral-500">{new Date(c.created_at).toLocaleDateString()}</span>
                {c.author_user_id === user?.id && (
                  <button data-testid={`delete-comment-${c.id}`} onClick={() => remove(c.id)} className="text-xs text-neutral-500 hover:text-red-500 uppercase tracking-widest">Delete</button>
                )}
              </div>
            </div>
            <p className="text-sm text-neutral-200 mt-2 whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Textarea data-testid={`comment-input-${focusItemId}`} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={user?.role === "coach" ? "Leave a coaching note…" : "Ask your coach a question…"}
          className="min-h-12 bg-neutral-950 border-neutral-700 flex-1" />
        <Button data-testid={`comment-post-${focusItemId}`} onClick={post} disabled={posting || !body.trim()} className="h-12 bg-red-600 hover:bg-red-700 font-semibold px-5 self-end">Post</Button>
      </div>
    </div>
  );
}
